import { getIn, useFormikContext } from 'formik';
import { FormEvent, useEffect, useState } from 'react';

import { SelectItem } from 'components/inputs/select/select';

import { FORMIK_INITIAL_VALUES } from '../../../../keys';
import { TIMEZONE_OPTIONS } from './timezones';
import { ToISOTimeParams, parseTimeFromISO, toISOTime } from './utils/time';

export function useTimeField(fieldPath: string) {
  const { values, setFieldValue } = useFormikContext<typeof FORMIK_INITIAL_VALUES>();
  const timeValue = getIn(values, fieldPath);

  const [localTime, setLocalTime] = useState<ToISOTimeParams>(() => parseTimeFromISO(timeValue || '', TIMEZONE_OPTIONS[0]));

  useEffect(() => {
    const parsed = parseTimeFromISO(timeValue || '', TIMEZONE_OPTIONS[0]);

    const same =
      parsed.hour === localTime.hour &&
      parsed.minute === localTime.minute &&
      parsed.period === localTime.period &&
      parsed.timezone.key === localTime.timezone.key;

    if (!same) {
      setLocalTime(parsed);
    }
  }, [timeValue]);

  const handleChange = (type: 'hour' | 'minute' | 'period', value: string) => {
    setLocalTime((prev) => {
      const updated = { ...prev, [type]: value };
      if (type === 'period') setFieldValue(fieldPath, toISOTime(updated));
      return updated;
    });
  };

  const handleBlur = () => {
    const hourNum = parseInt(localTime.hour || '0', 10);
    const minuteNum = parseInt(localTime.minute || '0', 10);

    const clamped: ToISOTimeParams = {
      hour: String(Math.max(1, Math.min(12, Number.isNaN(hourNum) ? 12 : hourNum))),
      minute: String(Math.max(0, Math.min(59, Number.isNaN(minuteNum) ? 0 : minuteNum))).padStart(2, '0'),
      period: localTime.period,
      timezone: localTime.timezone,
    };

    setLocalTime(clamped);
    setFieldValue(fieldPath, toISOTime(clamped));
  };

  const getBoundedInputHandler = (max: number) => (e: FormEvent<HTMLInputElement> & InputEvent) => {
    const input = e.target as HTMLInputElement;
    const next = input.value + (e as InputEvent).data;
    const num = parseInt(next, 10);
    if (Number.isNaN(num) || num > max) e.preventDefault();
  };

  const handleTimezoneChange = (item: SelectItem | SelectItem[] | null) => {
    const selected = Array.isArray(item) ? item[0] : item;
    if (!selected) return;
    const updated = { ...localTime, timezone: selected };
    setLocalTime(updated);
    setFieldValue(fieldPath, toISOTime(updated));
  };

  return {
    localTime,
    handleChange,
    handleBlur,
    getBoundedInputHandler,
    handleTimezoneChange,
  };
}
